import { useState, useEffect } from 'react'
import { Plug, CheckCircle2, Plus, X, Webhook, ArrowLeft } from 'lucide-react'
import { companiesApi } from '../lib/api'
import { useToast } from '../lib/ToastContext'
import { useAuth } from '../hooks/useAuth'

const APPS = [
  { id: 'quickbooks', name: 'QuickBooks', desc: 'Sync chart of accounts and invoices automatically.' },
  { id: 'zoho', name: 'Zoho', desc: 'Connect Zoho Books for expense and billing sync.' },
  { id: 'salesforce', name: 'Salesforce', desc: 'Push customer and revenue data to your CRM.' },
  { id: 'odoo', name: 'Odoo', desc: 'Two-way ERP sync for invoices, bills, and inventory.' },
]

const BIZOS_FIELDS = [
  'transaction.id',
  'transaction.date',
  'transaction.amount',
  'transaction.description',
  'transaction.category',
  'transaction.type',
  'company.name',
  'company.currency',
]

export default function Integrations({ onNavigate }) {
  const [connected, setConnected] = useState(() => {
    const saved = localStorage.getItem('bizos_integrations_connected')
    return saved ? JSON.parse(saved) : {}
  })
  const [showCustom, setShowCustom] = useState(false)
  const [customConnected, setCustomConnected] = useState(() => localStorage.getItem('bizos_integrations_custom') === 'true')
  const [endpoint, setEndpoint] = useState(() => localStorage.getItem('bizos_integrations_endpoint') || '')
  const [apiKey, setApiKey] = useState(() => localStorage.getItem('bizos_integrations_apikey') || '')
  const [mappings, setMappings] = useState({})
  const [connectModal, setConnectModal] = useState(null)
  const [modalApiKey, setModalApiKey] = useState('')
  const [modalUrl, setModalUrl] = useState('')
  const { toast } = useToast()
  const { company } = useAuth()
  const companyId = company?.id

  useEffect(() => {
    if (!companyId) return
    let mounted = true
    ;(async () => {
      const { data } = await companiesApi.get(companyId)
      if (!mounted || !data?.preferences) return
      const prefs = data.preferences
      if (prefs.connected) setConnected(prefs.connected)
      if (prefs.customConnected) setCustomConnected(prefs.customConnected)
      if (prefs.endpoint) setEndpoint(prefs.endpoint)
      if (prefs.mappings) setMappings(prefs.mappings)
    })()
    return () => { mounted = false }
  }, [companyId])

  const persistConnected = (next) => {
    setConnected(next)
    localStorage.setItem('bizos_integrations_connected', JSON.stringify(next))
  }

  const savePreferences = async (prefs) => {
    if (!companyId) return
    const { data: current } = await companiesApi.get(companyId)
    const existing = current?.preferences || {}
    await companiesApi.update(companyId, { preferences: { ...existing, ...prefs } })
  }

  const handleConnect = async (id) => {
    setConnectModal(id)
    setModalApiKey('')
    setModalUrl('')
  }

  const confirmConnect = async (e) => {
    e.preventDefault()
    if (!modalApiKey.trim()) { toast('API key is required.', 'error'); return }
    const id = connectModal
    const next = { ...connected, [id]: 'loading' }
    persistConnected(next)
    setConnectModal(null)
    setTimeout(async () => {
      const next2 = { ...connected, [id]: 'connected' }
      persistConnected(next2)
      await savePreferences({ connected: { ...connected, [id]: 'connected' } })
      toast(`${APPS.find((a) => a.id === id)?.name} connected successfully.`, 'success')
    }, 1500)
  }

  const handleDisconnect = async (id) => {
    const next = { ...connected }
    delete next[id]
    persistConnected(next)
    await savePreferences({ connected: { ...connected, [id]: undefined } })
    toast(`${APPS.find((a) => a.id === id)?.name} disconnected.`, 'info')
  }

  const handleCustomConnect = async () => {
    setCustomConnected(true)
    localStorage.setItem('bizos_integrations_custom', 'true')
    localStorage.setItem('bizos_integrations_endpoint', endpoint)
    localStorage.setItem('bizos_integrations_apikey', apiKey)
    await savePreferences({ customConnected: true, endpoint, apiKey, mappings })
    toast('Custom ERP/CRM connected successfully.', 'success')
    setTimeout(() => setShowCustom(false), 1500)
  }

  return (
    <div className="p-8 lg:p-10 space-y-8 animate-fade-in">
      <div>
        {/* {onNavigate && (
          <button onClick={() => onNavigate('dashboard')} className="btn-ghost text-xs flex items-center gap-1.5 mb-4">
            <ArrowLeft size={14} /> Back to Dashboard
          </button>
        )} */}
        <h1 className="text-2xl font-bold text-ink">Integrations</h1>
        <p className="text-sm text-muted mt-1">Connect your accounting and CRM tools for automatic sync.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {APPS.map((app) => {
          const state = connected[app.id]
          return (
            <div key={app.id} className="surface p-6 flex flex-col">
              <div className="flex items-start justify-between mb-4">
                <div className="w-10 h-10 border border-neutral-200 bg-neutral-50 flex items-center justify-center">
                  <Plug size={18} className="text-brand" />
                </div>
                {state === 'connected' && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 border border-emerald-300 bg-emerald-50 text-emerald-700 text-[11px] font-semibold">
                    <CheckCircle2 size={11} /> Connected &amp; Synced
                  </span>
                )}
              </div>
              <h3 className="text-sm font-semibold text-ink">{app.name}</h3>
              <p className="text-xs text-muted mt-1 mb-5 flex-1">{app.desc}</p>
              <button
                onClick={() => state === 'connected' ? handleDisconnect(app.id) : handleConnect(app.id)}
                disabled={state === 'loading'}
                className={state === 'connected' ? 'btn-secondary text-xs' : 'btn-primary text-xs'}
              >
                {state === 'loading' ? 'Connecting…' : state === 'connected' ? 'Disconnect' : 'Connect Tool'}
              </button>
            </div>
          )
        })}

        {/* Custom ERP/CRM card */}
        <div className="surface p-6 flex flex-col border-dashed">
          <div className="flex items-start justify-between mb-4">
            <div className="w-10 h-10 border border-neutral-200 bg-neutral-50 flex items-center justify-center">
              <Webhook size={18} className="text-brand" />
            </div>
            {customConnected && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 border border-emerald-300 bg-emerald-50 text-emerald-700 text-[11px] font-semibold">
                <CheckCircle2 size={11} /> Connected &amp; Synced
              </span>
            )}
          </div>
          <h3 className="text-sm font-semibold text-ink">Custom ERP / CRM</h3>
          <p className="text-xs text-muted mt-1 mb-5 flex-1">Connect any external system via webhook with field mapping.</p>
          <button
            onClick={() => setShowCustom(true)}
            className="btn-secondary text-xs flex items-center gap-1.5 justify-center"
          >
            <Plus size={14} /> Connect Custom ERP/CRM
          </button>
        </div>
      </div>

      {showCustom && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-ink/40" onClick={() => setShowCustom(false)} aria-hidden />
          <div className="relative bg-surface border-l border-neutral-200 w-full max-w-md h-full overflow-y-auto animate-fade-in">
            <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200 sticky top-0 bg-surface">
              <div className="flex items-center gap-2.5">
                <Webhook size={16} className="text-brand" />
                <h2 className="text-sm font-semibold text-ink">Connect Custom ERP / CRM</h2>
              </div>
              <button onClick={() => setShowCustom(false)} className="text-neutral-400 hover:text-ink" aria-label="Close">
                <X size={18} />
              </button>
            </div>

            <div className="px-6 py-5 space-y-5">
              <div>
                <label className="label-above">API Endpoint URL</label>
                <input
                  type="url"
                  value={endpoint}
                  onChange={(e) => setEndpoint(e.target.value)}
                  className="input-flat font-mono"
                  placeholder="https://api.your-erp.com/v1/webhook"
                />
              </div>
              <div>
                <label className="label-above">API Key / Authentication</label>
                <input
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  className="input-flat font-mono"
                  placeholder="sk-••••••••••••••••"
                />
              </div>

              <div>
                <label className="label-above">Data Mapping</label>
                <p className="text-[11px] text-muted mb-3">Map your external fields to Bizos fields.</p>
                <div className="space-y-2.5">
                  {BIZOS_FIELDS.map((field) => (
                    <div key={field} className="grid grid-cols-2 gap-2">
                      <div className="px-3 py-2 bg-neutral-50 border border-neutral-200 font-mono text-xs text-neutral-600">
                        {field}
                      </div>
                      <input
                        type="text"
                        value={mappings[field] || ''}
                        onChange={(e) => setMappings((p) => ({ ...p, [field]: e.target.value }))}
                        className="input-flat font-mono text-xs"
                        placeholder="external_field"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowCustom(false)} className="btn-secondary">Cancel</button>
                <button
                  onClick={handleCustomConnect}
                  disabled={!endpoint || !apiKey}
                  className="btn-primary"
                >
                  {customConnected ? 'Connected' : 'Connect'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {connectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-ink/40" onClick={() => setConnectModal(null)} />
          <div className="relative bg-surface border border-neutral-200 w-full max-w-sm animate-fade-in">
            <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
              <h2 className="text-sm font-semibold text-ink">Connect {APPS.find((a) => a.id === connectModal)?.name}</h2>
              <button onClick={() => setConnectModal(null)} className="text-neutral-400 hover:text-ink"><X size={18} /></button>
            </div>
            <form onSubmit={confirmConnect} className="px-6 py-5 space-y-4">
              <div>
                <label className="label-above">API Key <span className="text-rose-600 normal-case">*</span></label>
                <input className="input-flat font-mono" value={modalApiKey} onChange={(e) => setModalApiKey(e.target.value)} placeholder="Enter your API key" required />
              </div>
              <div>
                <label className="label-above">Server URL (optional)</label>
                <input className="input-flat font-mono" value={modalUrl} onChange={(e) => setModalUrl(e.target.value)} placeholder="https://your-instance.yopmail.com" />
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => setConnectModal(null)} className="btn-secondary flex-1">Cancel</button>
                <button type="submit" className="btn-primary flex-1">Connect</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
